/* eslint-env es2020 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaGift, FaShieldAlt, FaRocket, FaWallet, FaCoins, FaSearch, FaHandshake, FaArrowRight, FaExternalLinkAlt, FaBullhorn, FaBalanceScale } from 'react-icons/fa';
import './Validator.scss';
import Header from '../Header';
import Footer from '../Footer';
import TelegramPopup from '../../Common/TelegramPopup/TelegramPopup';
import reddickPointingRight from '../../../assets/reddickpointingright.png';

// Scroll-triggered reveal hook (identical to homepage)
const useScrollReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  const observe = useCallback(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin: '0px 0px -40px 0px' }
    );
    if (ref.current) observer.observe(ref.current);
    return observer;
  }, [threshold]);

  useEffect(() => {
    const observer = observe();
    return () => observer?.disconnect();
  }, [observe]);

  return [ref, isVisible];
};

const Validator = () => {
  const validatorAddress = 'validator_rdx1swywqhv3ed5w6dldfs2vamtrsyrdsfd6l9cqen5xqe54597gyckpns';
  const feePercent = 20;
  const paidOutPercent = 100 - feePercent;
  const payoutSchedule = 'the first Monday of each month';

  const [aboutRef, aboutVisible] = useScrollReveal(0.08);
  const [whyRef, whyVisible] = useScrollReveal(0.05);
  const [howRef, howVisible] = useScrollReveal(0.08);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Define static APY ranges
  const baseApyMin = 5.80;
  const baseApyMax = 6.20;
  // After the validator fee, stakers receive the remaining share of the base APY.
  const paidOutFraction = paidOutPercent / 100;
  const paidApyMin = baseApyMin * paidOutFraction;
  const paidApyMax = baseApyMax * paidOutFraction;

  const baseApyRangeString = `${baseApyMin.toFixed(2)}% - ${baseApyMax.toFixed(2)}%`;
  const paidApyRangeString = `${paidApyMin.toFixed(2)}% - ${paidApyMax.toFixed(2)}%`;

  return (
    <div className="validator-page" data-scroll-root>
      <Header />
      <main className="validator-content">

        {/* ============ HERO SECTION ============ */}
        <section className="val-hero">
          <div className="val-hero__bg-effects">
            <div className="val-hero__orb val-hero__orb--1"></div>
            <div className="val-hero__orb val-hero__orb--2"></div>
            <div className="val-hero__orb val-hero__orb--3"></div>
          </div>
          <div className="container">
            <div className="val-hero__content">
              <span className="val-hero__tagline">Official REDDICKS Validator</span>
              <h1 className="val-hero__title">
                <span className="val-hero__title-line">Support Reddicks</span>
                <span className="val-hero__title-accent">& Radix</span>
              </h1>
              <p className="val-hero__subtitle">Stake XRD with the official REDDICKS validator, support Radix decentralization, and receive $DCKS rewards. A {feePercent}% fee is applied, remaining rewards are used to buy DCKS, and payouts are sent to stakers on {payoutSchedule}.</p>
              <a
                href={`https://dashboard.radixdlt.com/network-staking/${validatorAddress}`}
                target="_blank"
                rel="noopener noreferrer"
                className="val-hero__cta"
              >
                Stake XRD Now <FaArrowRight className="val-hero__cta-arrow" />
              </a>
            </div>
          </div>
          <div className="val-hero__scroll-indicator">
            <span></span>
          </div>
        </section>

        {/* ============ ABOUT VALIDATOR SECTION ============ */}
        <section ref={aboutRef} className={`val-about ${aboutVisible ? 'visible' : ''}`}>
          <div className="section-divider"></div>
          <div className="container">
            <h2 className="section-title centered">What's a Validator & Why Stake?</h2>
            <div className="val-about__grid">
              <div className="val-about__image-card">
                <div className="val-about__image-glow"></div>
                <img src={reddickPointingRight} alt="Reddick pointing right explaining validators" />
              </div>
              <div className="val-about__text-card">
                <div className="val-about__text-card-accent"></div>
                <div className="val-about__text-card-content">
                  <p>On the Radix network, Validators are the backbone of security and operation. They run specialized servers (nodes) responsible for processing transactions, proposing new blocks of transactions, and maintaining the integrity of the distributed ledger. Think of them as the trusted guardians ensuring the network runs smoothly and securely.</p>
                  <p>When you "stake" your XRD tokens to a validator, you're essentially delegating your voting power to them. This signifies your trust in that validator and strengthens their ability to participate in Radix's Proof-of-Stake consensus mechanism - the process by which the network agrees on the valid order of transactions.</p>
                  <p><strong>Why does staking matter?</strong> By staking your XRD, you actively contribute to the decentralization and security of the Radix network. A widely distributed stake across many validators makes the network incredibly resilient against attacks. As a thank you for your contribution, you earn staking rewards!</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ WHY STAKE WITH REDDICKS SECTION ============ */}
        <section ref={whyRef} className={`val-why ${whyVisible ? 'visible' : ''}`}>
          <div className="section-divider"></div>
          <div className="container">
            <h2 className="section-title centered">Current REDDICKS Validator Terms</h2>
            <p className="val-why__intro">Choosing the REDDICKS validator means you support Radix security, the $DCKS ecosystem, and a healthier validator landscape where stake is not overly concentrated. This page is for XRD staking. To stake DCKS, use <Link to="/stake">RSwap DCKS staking</Link>.</p>

            <div className="val-why__update-card">
              <div className="val-why__update-kicker">
                <FaBullhorn />
                RedDicks Validator Update
              </div>
              <div className="val-why__update-main">
                <div>
                  <h3>10M XRD Subsidy Removed</h3>
                  <p>
                    The 10M XRD REDDICKS validator subsidy has been removed. XRD staking uses a {feePercent}% fee. After the fee, remaining funds are used to buy DCKS and sent to stakers on {payoutSchedule}.
                  </p>
                </div>
                <div className="val-why__update-badges" aria-label="Validator update highlights">
                  <span>{feePercent}% validator fee</span>
                  <span>{paidOutPercent}% paid in DCKS</span>
                  <span>First Monday DCKS payout</span>
                </div>
              </div>
              <div className="val-why__update-note">
                We are doing this in an attempt to make other smaller validators more competitive with REDDICKS. Thank you for choosing to support our vision!
              </div>
            </div>

            {/* APY Showcase */}
            <div className="val-why__apy-showcase">
              <div className="val-why__apy-showcase-glow"></div>
              <div className="val-why__apy-inner">
                <div className="val-why__apy-label">Estimated Base Reward Value Paid in $DCKS</div>
                <div className="val-why__apy-value">{paidApyRangeString}*</div>
                <div className="val-why__apy-breakdown">
                  ({paidOutPercent}% of a typical ~{baseApyRangeString} XRD APY range)
                </div>
              </div>
              <p className="val-why__apy-note">
                *A {feePercent}% validator fee is applied to validator rewards. The remaining {paidOutPercent}% is used to buy DCKS and sent to stakers on {payoutSchedule}. Delegators receive <strong>{paidOutPercent}% of the base XRD reward value</strong> in $DCKS. The live base APY can change with network conditions, so check the Radix Explorer for the current rate.
              </p>
              <div className="val-plain-language">
                <h4>Plain-Language Payout Summary</h4>
                <ul>
                  <li>A {feePercent}% fee is retained.</li>
                  <li>The remaining {paidOutPercent}% is used to buy DCKS and sent to stakers on {payoutSchedule}.</li>
                </ul>
              </div>
            </div>

            {/* Feature Cards */}
            <div className="val-why__grid">
              <div className="val-why__card" style={{ '--card-delay': '0s' }}>
                <div className="val-why__card-accent"></div>
                <div className="val-why__card-content">
                  <div className="val-why__card-icon val-why__card-icon--green">
                    <FaGift />
                  </div>
                  <h3>$DCKS Paid Monthly</h3>
                  <p>After the {feePercent}% fee, remaining rewards are used to buy DCKS and sent to stakers on {payoutSchedule}. Delegators receive {paidOutPercent}% of the base XRD reward value.</p>
                </div>
              </div>

              <div className="val-why__card" style={{ '--card-delay': '0.1s' }}>
                <div className="val-why__card-accent"></div>
                <div className="val-why__card-content">
                  <div className="val-why__card-icon val-why__card-icon--violet">
                    <FaBalanceScale />
                  </div>
                  <h3>Healthier Stake Distribution</h3>
                  <p>Removing the 10M XRD subsidy is intended to make other smaller validators more competitive with REDDICKS, leave more room across the set, and support network resilience, community growth, and decentralization.</p>
                </div>
              </div>

              <div className="val-why__card" style={{ '--card-delay': '0.2s' }}>
                <div className="val-why__card-accent"></div>
                <div className="val-why__card-content">
                  <div className="val-why__card-icon val-why__card-icon--cyan">
                    <FaShieldAlt />
                  </div>
                  <h3>Transparent & Reliable</h3>
                  <p>Current payouts use a simple model: a {feePercent}% validator fee, remaining funds used to buy DCKS, and distribution on {payoutSchedule}. Validator performance is visible on the Radix Explorer.</p>
                </div>
              </div>

              <div className="val-why__card" style={{ '--card-delay': '0.3s' }}>
                <div className="val-why__card-accent"></div>
                <div className="val-why__card-content">
                  <div className="val-why__card-icon val-why__card-icon--coral">
                    <FaRocket />
                  </div>
                  <h3>Simple & Secure Staking</h3>
                  <p>Delegate your XRD easily and securely using the official Radix Wallet. Your tokens always remain in your control while your stake contributes to Radix network security.</p>
                </div>
              </div>
            </div>

            {/* Validator Address */}
            <div className="val-why__address">
              <h3>Our Validator Address:</h3>
              <p className="val-why__address-text">{validatorAddress}</p>
              <a
                href={`https://dashboard.radixdlt.com/network-staking/${validatorAddress}`}
                target="_blank"
                rel="noopener noreferrer"
                className="val-why__address-link"
              >
                Verify & Stake on Radix Explorer <FaExternalLinkAlt />
              </a>
            </div>
          </div>
        </section>

        {/* ============ HOW TO STAKE SECTION ============ */}
        <section ref={howRef} className={`val-how ${howVisible ? 'visible' : ''}`}>
          <div className="section-divider"></div>
          <div className="container">
            <h2 className="section-title centered">How to Stake Your XRD</h2>
            
            <div className="val-how__path">
              <div className="val-how__path-line"></div>
              
              <div className="val-how__step" style={{ '--step-delay': '0s' }}>
                <div className="val-how__step-node">
                  <div className="val-how__step-node-inner">
                    <FaWallet />
                  </div>
                  <div className="val-how__step-node-glow"></div>
                </div>
                <div className="val-how__step-card">
                  <div className="val-how__step-card-glow"></div>
                  <h3>Get the Radix Wallet</h3>
                  <p>If you haven't already, download and set up the official Radix Wallet on your mobile device. Remember to securely back up your seed phrase!</p>
                  <a href="https://wallet.radixdlt.com/" target="_blank" rel="noopener noreferrer" className="val-how__step-link">
                    Get Radix Wallet <FaArrowRight />
                  </a>
                </div>
              </div>

              <div className="val-how__step" style={{ '--step-delay': '0.15s' }}>
                <div className="val-how__step-node">
                  <div className="val-how__step-node-inner">
                    <FaCoins />
                  </div>
                  <div className="val-how__step-node-glow"></div>
                </div>
                <div className="val-how__step-card">
                  <div className="val-how__step-card-glow"></div>
                  <h3>Fund Your Wallet</h3>
                  <p>Acquire XRD tokens through a supported exchange (like Bitfinex, MEXC, etc.) and transfer them to your Radix Wallet address.</p>
                </div>
              </div>

              <div className="val-how__step" style={{ '--step-delay': '0.3s' }}>
                <div className="val-how__step-node">
                  <div className="val-how__step-node-inner">
                    <FaSearch />
                  </div>
                  <div className="val-how__step-node-glow"></div>
                </div>
                <div className="val-how__step-card">
                  <div className="val-how__step-card-glow"></div>
                  <h3>Find the REDDICKS Validator</h3>
                  <p>Inside the Radix Wallet app, navigate to the 'Stake & Unstake' section. Use the search function to find the REDDICKS validator by name or paste our address:</p>
                  <code className="val-how__validator-code">{validatorAddress}</code>
                </div>
              </div>

              <div className="val-how__step" style={{ '--step-delay': '0.45s' }}>
                <div className="val-how__step-node">
                  <div className="val-how__step-node-inner">
                    <FaHandshake />
                  </div>
                  <div className="val-how__step-node-glow"></div>
                </div>
                <div className="val-how__step-card">
                  <div className="val-how__step-card-glow"></div>
                  <h3>Delegate Your Stake</h3>
                  <p>Select the REDDICKS validator, enter the amount of XRD you wish to stake, and confirm the transaction. After the {feePercent}% fee, remaining rewards are used to buy DCKS and sent on {payoutSchedule}.</p>
                  <a
                    href={`https://dashboard.radixdlt.com/network-staking/${validatorAddress}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="val-how__step-link val-how__step-link--cta"
                  >
                    Stake via Explorer <FaArrowRight />
                  </a>
                </div>
              </div>
            </div>

            <p className="val-how__note">Note: You need a small amount of XRD for transaction fees. DCKS payouts are sent on {payoutSchedule}.</p>
          </div>
        </section>

      </main>
      <Footer />
      <TelegramPopup />
    </div>
  );
};

export default Validator;
